import { seekVideoPromise } from "@/lib/video-timeline"

/** Thrown when frame grab needs a same-origin proxy but none is available / canvas is tainted. */
export const VIDEO_CAPTURE_UNAVAILABLE = "VIDEO_CAPTURE_UNAVAILABLE"

/**
 * Offscreen CORS-safe frame grab from a same-origin (or CORS) video URL.
 * Used when the playback element uses stream_url that 302→S3 and would taint canvas.
 */
export async function captureFrameFromCorsVideo(url: string, timeSec: number): Promise<HTMLImageElement> {
  const video = document.createElement("video")
  video.crossOrigin = "anonymous"
  video.preload = "auto"
  video.muted = true
  video.playsInline = true

  try {
    await new Promise<void>((resolve, reject) => {
      const onReady = () => {
        cleanup()
        resolve()
      }
      const onError = () => {
        cleanup()
        reject(new Error(VIDEO_CAPTURE_UNAVAILABLE))
      }
      const cleanup = () => {
        video.removeEventListener("loadeddata", onReady)
        video.removeEventListener("error", onError)
      }
      video.addEventListener("loadeddata", onReady)
      video.addEventListener("error", onError)
      video.src = url
      video.load()
    })

    await seekVideoPromise(video, timeSec)

    const w = video.videoWidth
    const h = video.videoHeight
    if (!w || !h) throw new Error(VIDEO_CAPTURE_UNAVAILABLE)

    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext("2d")
    if (!ctx) throw new Error("Canvas unavailable")

    try {
      ctx.drawImage(video, 0, 0, w, h)
    } catch {
      throw new Error(VIDEO_CAPTURE_UNAVAILABLE)
    }

    const blob = await new Promise<Blob | null>((resolve) => {
      try {
        canvas.toBlob(resolve, "image/png", 0.92)
      } catch {
        resolve(null)
      }
    })
    if (!blob) throw new Error(VIDEO_CAPTURE_UNAVAILABLE)

    const objectUrl = URL.createObjectURL(blob)
    try {
      return await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error("Frame capture failed"))
        img.src = objectUrl
      })
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  } finally {
    video.removeAttribute("src")
    video.load()
  }
}
